import {dueWithinBounds} from 'client/TeacherDashboard/SkillPaths/dueWindow';
import {toClassroomIds} from 'client/TeacherDashboard/SkillPaths/hooks/skillPathEntryFilters';
import type {
  DueWithinFilter,
  SkillPathRowStatus,
  SkillPathSortValue,
  SkillPathStatusSegment
} from 'client/TeacherDashboard/SkillPaths/types';
import type {SortValue} from 'client/TeacherDashboard/types';
import {ilikeAnywhere} from 'lib/gqlFilters';
import type {DiagnosticFilterInput, DiagnosticSortInput} from 'gql/types';
import {DiagnosticSortField, DiagnosticStatus, SortOrder} from 'gql/types';

export const STATUS_ENUM: Record<SkillPathRowStatus, DiagnosticStatus> = {
  open: DiagnosticStatus.Open,
  closed: DiagnosticStatus.Closed,
  scheduled: DiagnosticStatus.Scheduled
};

/** The status a tab scopes its dates and counts to; `all` sends none, spanning every group. */
export const toStatusArg = (segment: SkillPathStatusSegment): DiagnosticStatus | null =>
  segment === 'all' ? null : STATUS_ENUM[segment];

/** Everything except the status segment — shared by the visible list and the per-status counts. */
export interface SharedDiagnosticFilters {
  classroomId?: string;
  search: string;
  /** Subject id, or `'all'`. */
  subject: string;
  dueWithin: DueWithinFilter;
  dueDateStart: string;
  dueDateEnd: string;
}

/**
 * Row inclusion, per status. Omitting `classroomIds` is not "no scope" — it means the teacher's
 * *active* classes, which is what keeps an archived-only diagnostic out of All Classes while leaving
 * it visible in that class's own scope. Date bounds match on overlap, as the sibling lists do.
 */
export const buildDiagnosticFilter = (
  {classroomId, search, subject, dueWithin, dueDateStart, dueDateEnd}: SharedDiagnosticFilters,
  segment: SkillPathStatusSegment
): DiagnosticFilterInput => {
  const classroomIds = toClassroomIds(classroomId);
  const status = toStatusArg(segment);
  const scopeInput = classroomIds ? {classroomIds} : undefined;

  // `all` sends no status clause at all: naming every status would exclude nothing while making
  // Postgres evaluate a cond of correlated EXISTS over every candidate row.
  const filter: DiagnosticFilterInput = {
    assignedToClassrooms: {eq: true, input: scopeInput}
  };

  if (status) {
    filter.status = {eq: status, input: scopeInput};
  }
  const searchTerm = search.trim();
  if (searchTerm) {
    filter.name = {ilike: ilikeAnywhere(searchTerm)};
  }
  if (subject !== 'all') {
    filter.guide = {subjectId: {eq: subject}};
  }

  const dateInput = {...(classroomIds ? {classroomIds} : {}), status};
  const {start, end} = dueWithinBounds(dueWithin, dueDateStart, dueDateEnd);
  if (start) {
    filter.maxDueDate = {greaterThanOrEqual: start.toISOString(), input: dateInput};
  }
  if (end) {
    filter.minDueDate = {lessThanOrEqual: end.toISOString(), input: dateInput};
  }
  return filter;
};

/** Every option sends an explicit sort, so the direction toggle means something for all of them. */
export const buildDiagnosticSort = (
  {field, order}: SortValue<SkillPathSortValue>,
  segment: SkillPathStatusSegment,
  classroomId?: string
): DiagnosticSortInput[] => {
  const sortOrder = order === 'desc' ? SortOrder.Desc : SortOrder.Asc;
  const classroomIds = toClassroomIds(classroomId);
  // Sorting by a scoped date has to scope the key too, or rows order by a value they don't display.
  // The All tab names no single status, so its date sorts use the keys that scope to each row's own.
  const scopeInput = classroomIds ? {classroomIds} : {};
  const dateInput = {...scopeInput, status: toStatusArg(segment)};

  switch (field) {
    case 'recentlyEdited':
      return [
        {field: DiagnosticSortField.UpdatedAt, order: sortOrder},
        {field: DiagnosticSortField.Name, order: SortOrder.Asc}
      ];
    case 'name':
      return [{field: DiagnosticSortField.Name, order: sortOrder}];
    case 'startDate':
      return [
        segment === 'all'
          ? {
              field: DiagnosticSortField.StatusScopedMinStartDate,
              order: sortOrder,
              statusScopedMinStartDateInput: scopeInput
            }
          : {
              field: DiagnosticSortField.MinStartDate,
              order: sortOrder,
              minStartDateInput: dateInput
            },
        {field: DiagnosticSortField.Name, order: SortOrder.Asc}
      ];
    case 'dueDate':
    default:
      return [
        segment === 'all'
          ? {
              field: DiagnosticSortField.StatusScopedMinDueDate,
              order: sortOrder,
              statusScopedMinDueDateInput: scopeInput
            }
          : {field: DiagnosticSortField.MinDueDate, order: sortOrder, minDueDateInput: dateInput},
        {field: DiagnosticSortField.Name, order: SortOrder.Asc}
      ];
  }
};
